import React, { useCallback, useState } from "react";

import cx from "classnames";

import {
  coalesce,
  doNothing,
  filterUndefined,
  getClosureFor,
} from "@kazoohr/helpers";

import { Autocomplete } from "../Autocomplete";
import { IconDictionary } from "../Icon";
import { Text } from "../Text";
import { UserDisplay } from "../UserDisplay";

import { User } from "./UserSelect";
import styles from "./UserSelect.scss";
import { UserSelectInput } from "./UserSelectInput";

interface AccessibleMessageProps {
  children: string;
}

function AccessibleMessage({ children }: AccessibleMessageProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      key={children}
      className={styles["accessible-message"]}
    >
      {children}
    </div>
  );
}

interface AutocompleteRenderInputProps {
  ref: (node: HTMLInputElement | null) => void;
  highlightedIndex: number | null;
  [key: string]: any;
}

export interface RawUserSelectProps
  extends React.HTMLAttributes<HTMLInputElement> {
  /**
   * Currently selected user
   */
  selectedUser?: User;
  /**
   * Array of currently selected users (for multi-select)
   */
  selectedUsers?: User[];
  /**
   * Whether to show the user icon
   */
  showIcon?: boolean;
  /**
   * Icon to be used for the input
   */
  icon?: IconDictionary;
  /**
   * Callback when input text changes
   */
  onTextChange?: (text: string) => void;
  /**
   * Callback when a user is selected
   */
  onUserSelected?: (user: User) => void;
  /**
   * Callback when selected user is cleared
   */
  onUserCleared?: (user: User) => void;
  /**
   * Array of users to select from
   */
  users?: User[];
  /**
   * Whether multiple users can be selected
   */
  multipleUsers?: boolean;
  /**
   * Maximum number of users that can be selected
   */
  maxUsers?: number;
  /**
   * Whether or not the menu should be rendered in a portal
   */
  renderInPortal?: boolean;
}

/**
 * Raw user select component that provides the core user selection functionality.
 * Used internally by UserSelect.
 */
// eslint-disable-next-line max-lines-per-function
export function RawUserSelect({
  selectedUser,
  selectedUsers = [],
  showIcon,
  icon,
  onTextChange = doNothing,
  onUserSelected = doNothing,
  onUserCleared = doNothing,
  users = [],
  multipleUsers,
  maxUsers,
  renderInPortal = true,
  ...inputProps
}: RawUserSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [userInputRef, setUserInputRef] = useState<HTMLInputElement | null>(
    null
  );
  const [userContainerRef, setUserContainerRef] =
    useState<HTMLDivElement | null>(null);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setInputValue(value);
      onTextChange(value);
    },
    [onTextChange]
  );

  const getDropdownItemClassName = useCallback((isHighlighted: boolean) => {
    return cx(styles["dropdown-item"], {
      [styles["dropdown-item--focused"]]: isHighlighted,
    });
  }, []);

  const getDropdownMenuClassName = useCallback(
    (items: React.ReactNode[]) => {
      return cx(styles["dropdown-menu"], {
        [styles["dropdown-menu--multi"]]: multipleUsers,
        [styles["dropdown-menu--with-items"]]: items.length > 0,
      });
    },
    [multipleUsers]
  );

  const accessibleResultsMessage = inputValue
    ? `${users.length} ${users.length > 1 ? "Results" : "Result"} Available`
    : "";

  const selectedUsersArray = filterUndefined(
    multipleUsers ? selectedUsers : [selectedUser]
  );

  const renderUserItem = (user: User, isHighlighted: boolean) => {
    const userIndex = users.findIndex(({ id }) => id === user.id);
    const accessibleUserMessage = `${user.name} ${coalesce(
      user.jobTitle,
      ""
    )} ${coalesce(user.primaryGroup, "")} (${userIndex + 1} of ${
      users.length
    })`;
    return (
      <li
        key={user.id}
        className={getDropdownItemClassName(isHighlighted)}
        role="option"
        aria-selected={isHighlighted}
        onClick={getClosureFor(onUserSelected, user)}
      >
        <div className={styles["user-display"]}>
          <UserDisplay
            userProfile={{
              ...user,
              jobTitle:
                typeof user.jobTitle === "string"
                  ? user.jobTitle
                  : user.jobTitle?.name,
              primaryGroup:
                typeof user.primaryGroup === "string"
                  ? user.primaryGroup
                  : user.primaryGroup?.name,
            }}
            withDetails
          />
        </div>
        {isHighlighted && (
          <AccessibleMessage>{accessibleUserMessage}</AccessibleMessage>
        )}
      </li>
    );
  };

  return (
    <>
      <Autocomplete
        renderInPortal={renderInPortal}
        popperEnabled
        wrapperStyle={{ position: "relative" }}
        items={users}
        value={selectedUser ? selectedUser.name : inputValue}
        getItemValue={(user: User) => user.name}
        onChange={handleInputChange}
        inputProps={inputProps}
        renderInput={({
          ref: autocompleteRef,
          ...autocompleteInputProps
        }: Omit<AutocompleteRenderInputProps, "highlightedIndex">) => {
          return (
            <UserSelectInput
              inputRef={(ref) => {
                setUserInputRef(ref);
                autocompleteRef(ref);
              }}
              inputProps={autocompleteInputProps}
              innerRef={(ref) => setUserContainerRef(ref)}
              multipleUsers={multipleUsers}
              onUserCleared={onUserCleared}
              selectedUsers={selectedUsersArray}
              showIcon={showIcon}
              icon={icon}
              maxUsers={Boolean(maxUsers && selectedUsers.length >= maxUsers)}
            />
          );
        }}
        renderMenu={(items: React.ReactNode[]) => {
          let left: number | undefined;
          if (multipleUsers && userContainerRef && userInputRef) {
            left =
              userInputRef.getBoundingClientRect().left -
              userContainerRef.getBoundingClientRect().left;
          }
          return (
            <ul
              className={getDropdownMenuClassName(items)}
              style={{ left: left || 0 }}
              role="listbox"
            >
              {items}
            </ul>
          );
        }}
        renderItem={renderUserItem}
        onSelect={(value: string, user: User) => {
          onUserSelected(user);
          setInputValue("");
        }}
      />
      <AccessibleMessage>{accessibleResultsMessage}</AccessibleMessage>
      {maxUsers && (
        <div className={styles["user-counter-container"]}>
          <Text
            className={styles["user-counter"]}
            size="extraSmall"
            color="#666666"
          >
            {selectedUsers.length}/{maxUsers}
          </Text>
        </div>
      )}
    </>
  );
}
